import { JSONSchema7 } from "json-schema"
import { getObjectKeysSafely } from "../../../../Helpers/Functions/object"
import { Button, Text } from "dash-styled"
import { FC, useCallback, useEffect, useMemo, useRef } from "react"
import type { SchemaFormRefProps } from "resha"
import { useAppState } from "../../../../Hooks/useReduxState"
import { SchemaForm } from "../../Lazy/SchemaForm"
import type { UiSchema } from "@rjsf/core"
import { useDispatch } from "react-redux"
import { MainThunkDispatch } from "../../../../Reducers"
import { changeInputValue } from "../../../../Actions/Client/InputRender/InputRenderActions"

const FORM_NAME = (id: string) => `${id}_datamapper`

interface FormData {
    [mapTo: string]: string
}

interface Props {
    schema: JSONSchema7
    data: any[]
    id: string
    onMapped: (mappedData: any[]) => void
}

const noop = () => Promise.resolve("")

const DataMapperIsolated: FC<Props> = ({
    id,
    onMapped,
    schema: dataSchema,
    data
}) => {
    const dispatch = useDispatch<MainThunkDispatch>()
    const formRef = useRef<SchemaFormRefProps>(null)
    const formData = useAppState(state => state.client.inputRender.entries[id])

    const formDataRef = useRef(formData)

    const dataForChoices = data[0]

    const { schema, uiSchema, defaultData } = useMemo<{
        schema: JSONSchema7
        uiSchema: UiSchema
        defaultData: any
    }>(() => {
        const choices = getObjectKeysSafely(dataForChoices) || []

        const [properties, defaultData] = (
            getObjectKeysSafely(dataSchema.properties) || []
        ).reduce<[JSONSchema7, { [key: string]: unknown }]>(
            ([passedProperties, passedDefaultData], key) => {
                return [
                    {
                        ...passedProperties,
                        [key]: { type: "string", enum: choices, title: key }
                    },
                    {
                        ...passedDefaultData,
                        [key]: undefined
                    }
                ]
            },
            [{}, {}]
        )
        return {
            schema: { type: "object", properties },
            uiSchema: {},
            defaultData
        } as { schema: JSONSchema7; uiSchema: UiSchema; defaultData: any }
    }, [dataSchema.properties, dataForChoices])

    const handleChange = useCallback(
        (data: unknown) => {
            dispatch(
                changeInputValue(id, {
                    values: data,
                    surviveReroute: true,
                    formErrors: [],
                    errors: []
                })
            )
        },
        [id, dispatch]
    )

    const handleSubmit = useCallback(
        (values: unknown) => {
            const mapping = new Map()

            const keys2 = getObjectKeysSafely(values)
            if (!keys2) return

            keys2.forEach(mapTo => {
                const mapFrom = (values as FormData)?.[mapTo]

                if (mapFrom === undefined) return

                mapping.set(mapFrom, mapTo)
            })

            const mappedData = data.map(toMap => {
                const mapped = {} as any
                mapping.forEach((value, key) => (mapped[value] = toMap[key]))
                return mapped
            })

            onMapped(mappedData)
        },
        [onMapped, data]
    )

    const handleError = useCallback(() => {
        handleSubmit(formDataRef.current?.values ?? defaultData)
    }, [handleSubmit, defaultData])

    useEffect(() => {
        formDataRef.current = formData
    }, [formData])

    return (
        <div className="w-full">
            <SchemaForm
                className="w-full"
                id={id}
                ref={formRef}
                schema={schema}
                uiSchema={uiSchema}
                formData={formData?.values ?? defaultData}
                skipMissingDataField={false}
                shouldRenderFieldInPanel={true}
                urlSigner={noop}
                enableImageTools={false}
                onChange={handleChange}
                onError={handleError}
                onSubmit={handleSubmit}
            />
            <Button
                onClick={() => formRef.current?.submit()}
                color="darkviolet"
                variant="contained"
                disabled={formData === undefined || formData.errors.length > 0}
                marginTop="5"
            >
                Map Data
            </Button>
        </div>
    )
}

// get map-to values
// select from list of files (array names)
// get first item of data array
// try to auto map values
// show empty on values with no mapping
// apply to all (validate against jsonschema) (preserve index of failed mappings)
// return to step 3
// return to step 2
export const DataMapper = ({ schema, id, ...rest }: Props) => {
    if (schema.type !== "object") {
        return <Text>Schema not correct type, expected an object.</Text>
    }

    return <DataMapperIsolated id={FORM_NAME(id)} schema={schema} {...rest} />
}
